import { useCallback, useEffect, useState } from 'react'
import { Clock, PauseCircle, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  formatSuspendedAt,
  formatSuspendedGuestLabel,
  formatSuspendedTicketLabel,
  formatSuspendedTotal,
} from '@/components/pos/posHold'
import { cn } from '@/lib/utils'

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {import('@/types/api').SuspendedSaleSummary[]} props.sales
 * @param {boolean} props.loading
 * @param {boolean} props.submitting
 * @param {(saleId: number) => Promise<boolean>} props.onRecall
 * @param {string} [props.error]
 */
export function PosSuspendedModal({
  open,
  onClose,
  sales,
  loading,
  submitting,
  onRecall,
  error = '',
}) {
  const [selectedId, setSelectedId] = useState(null)

  useEffect(() => {
    if (!open) {
      setSelectedId(null)
    }
  }, [open])

  const handleRecall = useCallback(async () => {
    if (!selectedId) return
    const recalled = await onRecall(selectedId)
    if (recalled) {
      onClose()
    }
  }, [onRecall, onClose, selectedId])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 p-4 backdrop-blur-sm"
      role="presentation"
      onClick={submitting ? undefined : onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pos-suspended-title"
        className="card-surface flex max-h-[min(80vh,640px)] w-full max-w-lg flex-col overflow-hidden shadow-luxury-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-border/50 px-4 py-3">
          <div>
            <p id="pos-suspended-title" className="text-sm font-semibold">
              Load ticket
            </p>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              Held tickets only — use Load appointment (F3) for today&apos;s bookings.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {error ? (
          <p className="border-b border-destructive/20 bg-destructive/5 px-4 py-2 text-[11px] text-destructive">
            {error}
          </p>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="h-16 rounded-xl skeleton-shimmer" aria-hidden />
              ))}
            </div>
          ) : sales.length === 0 ? (
            <p className="rounded-xl border border-dashed border-border/60 bg-muted/20 px-4 py-8 text-center text-sm text-muted-foreground">
              No held tickets for this branch.
              <span className="mt-2 block text-xs">
                Use Hold (F5) to park the current ticket, then load it here.
              </span>
            </p>
          ) : (
            <ul className="space-y-2">
              {sales.map((summary) => {
                const isSelected = selectedId === summary.id
                return (
                  <li key={summary.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(summary.id)}
                      className={cn(
                        'flex w-full items-start gap-3 rounded-xl border px-3 py-3 text-left transition-all',
                        isSelected
                          ? 'border-primary/40 bg-primary/5 shadow-sm'
                          : 'border-border/50 bg-card hover:border-primary/20',
                      )}
                    >
                      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-primary">
                        <PauseCircle className="h-4 w-4" aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[12px] font-semibold">
                          {formatSuspendedTicketLabel(summary)}
                        </span>
                        <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-muted-foreground">
                          <span>{formatSuspendedGuestLabel(summary)}</span>
                          <span aria-hidden>·</span>
                          <span className="inline-flex items-center gap-1">
                            <Clock className="h-3 w-3" aria-hidden />
                            {formatSuspendedAt(summary.suspended_at)}
                          </span>
                        </span>
                      </span>
                      <span className="shrink-0 text-[12px] font-bold text-gold">
                        {formatSuspendedTotal(summary)}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className="flex gap-2 border-t border-border/50 bg-muted/20 p-3">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="flex-1"
            disabled={!selectedId || submitting}
            onClick={handleRecall}
          >
            Load ticket
          </Button>
        </div>
      </div>
    </div>
  )
}
